import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Loader2, RefreshCw, ShieldCheck, Search } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const ACTIONS = {
  create: { ar: "إضافة", en: "Create", cls: "bg-emerald-100 text-emerald-800" },
  approve: { ar: "اعتماد", en: "Approve", cls: "bg-sky-100 text-sky-800" },
  approve_all: { ar: "اعتماد الكل", en: "Approve all", cls: "bg-sky-100 text-sky-800" },
  delete: { ar: "حذف", en: "Delete", cls: "bg-rose-100 text-rose-800" },
  dedup_delete: { ar: "حذف مكرر", en: "Dedup delete", cls: "bg-rose-100 text-rose-800" },
  ai_recover: { ar: "تصحيح بالذكاء", en: "AI recover", cls: "bg-amber-100 text-amber-800" },
};

export default function AdminAuditLog() {
  const { lang } = useI18n();
  const { user, loading } = useAuth();
  const isAr = lang === "ar";

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(true);
  const [action, setAction] = useState("");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams({ limit: "300" });
      if (action) params.set("action", action);
      if (q.trim()) params.set("q", q.trim());
      const r = await api.get(`/admin/audit-logs?${params.toString()}`);
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch {
      setItems([]);
    } finally {
      setBusy(false);
    }
  }, [action, q]);

  useEffect(() => {
    if (user?.role === "admin") load();
  }, [user, load]);

  if (loading) return <div className="p-20 text-center text-slate-500 text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login?next=/admin/audit-log" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  const fmt = (ts) => {
    try {
      return new Date(ts).toLocaleString(isAr ? "ar-KW" : "en-GB", {
        dateStyle: "medium", timeStyle: "short",
      });
    } catch { return ts; }
  };

  return (
    <div data-testid="admin-audit-log" dir={isAr ? "rtl" : "ltr"}>
      <div className="flex items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-slate-700" />
          <h1 className="font-display text-2xl text-slate-900 dark:text-slate-100">
            {isAr ? "سجل العمليات" : "Audit Log"}
          </h1>
          <span className="text-sm text-slate-500">({total})</span>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="audit-refresh">
          <RefreshCw className="w-4 h-4 me-1" /> {isAr ? "تحديث" : "Refresh"}
        </Button>
      </div>

      <p className="text-sm text-slate-500 mb-4">
        {isAr
          ? "سجل بكل عمليات الأدمن على القوانين: مَن أضاف/اعتمد/حذف/صحّح قانوناً ومتى."
          : "Record of every admin action on laws — who created / approved / deleted / recovered a law and when."}
      </p>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute start-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder={isAr ? "ابحث بعنوان القانون أو البريد…" : "Search by law title or email…"}
            data-testid="audit-search"
            className="ps-9 h-10 rounded-sm"
          />
        </div>
        <select
          value={action}
          onChange={(e) => setAction(e.target.value)}
          data-testid="audit-action-filter"
          className="h-10 rounded-sm border px-3 text-sm bg-white dark:bg-slate-900"
          style={{ borderColor: "var(--line-strong, #cbd5e1)" }}
        >
          <option value="">{isAr ? "كل العمليات" : "All actions"}</option>
          {Object.entries(ACTIONS).map(([k, v]) => (
            <option key={k} value={k}>{isAr ? v.ar : v.en}</option>
          ))}
        </select>
      </div>

      {busy ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm py-10 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> {isAr ? "جارٍ التحميل…" : "Loading…"}
        </div>
      ) : items.length === 0 ? (
        <div data-testid="audit-empty" className="text-center text-slate-500 text-sm py-16 border rounded-sm">
          {isAr ? "لا توجد عمليات مسجّلة بعد." : "No actions logged yet."}
        </div>
      ) : (
        <div className="border rounded-sm overflow-hidden" style={{ borderColor: "var(--line, #e2e8f0)" }}>
          <table className="w-full text-sm" data-testid="audit-table">
            <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500">
              <tr>
                <th className="text-start px-4 py-2.5 font-medium">{isAr ? "الوقت" : "Time"}</th>
                <th className="text-start px-4 py-2.5 font-medium">{isAr ? "العملية" : "Action"}</th>
                <th className="text-start px-4 py-2.5 font-medium">{isAr ? "القانون" : "Law"}</th>
                <th className="text-start px-4 py-2.5 font-medium">{isAr ? "المستخدم" : "By"}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const a = ACTIONS[it.action] || { ar: it.action, en: it.action, cls: "bg-slate-100 text-slate-700" };
                return (
                  <tr key={it.id} data-testid="audit-row" className="border-t" style={{ borderColor: "var(--line, #e2e8f0)" }}>
                    <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">{fmt(it.ts)}</td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${a.cls}`}>
                        {isAr ? a.ar : a.en}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-slate-800 dark:text-slate-200 max-w-[360px] truncate">
                      {it.title || (it.meta?.count ? `${it.meta.count} ${isAr ? "قانون" : "laws"}` : "—")}
                    </td>
                    <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">
                      {it.actor_name || it.actor_email || "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
